import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CircleNotch } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QueueRow({ e, productId, onResolved }) {
  const [busy, setBusy] = useState(null);
  const [keeping, setKeeping] = useState(false);
  const [justification, setJustification] = useState("");

  const resolve = async (action) => {
    if (action === "keep_assigned" && !justification.trim()) {
      toast.error("A justification is required to keep the assigned design.");
      return;
    }
    setBusy(action);
    try {
      await axios.post(`${API}/design/${productId}/queue/${e.asset_id}/resolve`,
        { action, justification: justification.trim() || null });
      toast.success(action === "accept_suggested" ? `Reclassified as ${e.suggested_label}` : "Assigned design kept with justification");
      onResolved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not resolve the correction.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={`design-correction-${e.asset_id}`} className="px-5 py-3.5 border-b border-slate-100 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-800">{e.asset_name}</p>
          <p className="text-[10px] text-slate-500 mt-1">
            Assigned: <span className="font-bold text-red-700">{e.assigned_label}</span>
            {" → "}Suggested: <span className="font-bold text-emerald-700">{e.suggested_label}</span>
          </p>
          {e.reasons.map((r, i) => (
            <p key={i} className="text-[10px] text-amber-700 mt-0.5">• {r}</p>
          ))}
          {e.assigned_category === "network_meta_analysis" && (
            <p className="text-[10px] text-slate-400 mt-0.5">
              NMA evidence — connected treatment network: {e.nma_evidence?.connected_treatment_network ? "yes" : "no"} ·
              formal network synthesis: {e.nma_evidence?.formal_network_synthesis ? "yes" : "no"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid={`accept-suggested-${e.asset_id}`} disabled={!!busy}
            onClick={() => resolve("accept_suggested")}
            className="text-[11px] font-semibold px-3 py-1.5 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">
            {busy === "accept_suggested" ? <CircleNotch className="animate-spin inline" size={12} /> : "Accept suggested"}
          </button>
          <button data-testid={`keep-assigned-${e.asset_id}`} disabled={!!busy}
            onClick={() => setKeeping(!keeping)}
            className="text-[11px] font-semibold px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Keep assigned
          </button>
        </div>
      </div>
      {keeping && (
        <div className="flex items-center gap-2 mt-2">
          <input data-testid={`keep-justification-${e.asset_id}`} value={justification}
            onChange={(ev) => setJustification(ev.target.value)}
            placeholder="Justification required to keep the assigned design…"
            className="flex-1 text-xs border border-slate-300 px-3 py-1.5 focus:outline-none focus:border-slate-500" />
          <button data-testid={`confirm-keep-${e.asset_id}`} disabled={!!busy}
            onClick={() => resolve("keep_assigned")}
            className="text-[11px] font-semibold px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">
            {busy === "keep_assigned" ? <CircleNotch className="animate-spin inline" size={12} /> : "Confirm keep"}
          </button>
        </div>
      )}
    </div>
  );
}

export const DesignCorrections = ({ productId, onAssetsChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/design/${productId}/queue`)
      .then((r) => { setData(r.data); setError(false); })
      .catch(() => setError(true));
  }, [productId]);

  useEffect(() => { setData(null); load(); }, [load]);

  const onResolved = () => { load(); onAssetsChanged?.(); };

  return (
    <section data-testid="design-corrections" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <p className="font-heading font-bold text-sm tracking-tight">Design Classification Corrections</p>
          <p className="text-xs text-slate-500 mt-1">Records whose assigned design conflicts with their extracted methods. A record cannot be a Network Meta-Analysis without a connected treatment network and formal network synthesis.</p>
        </div>
        {data && <span data-testid="design-queue-count" className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${data.pending.length ? "bg-amber-50 text-amber-700 border border-amber-300" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
          {data.pending.length} pending
        </span>}
      </div>
      {error ? (
        <p data-testid="design-queue-error" className="px-5 py-6 text-center text-xs text-red-600">Could not load the correction queue. <button onClick={load} className="underline">Retry</button></p>
      ) : !data ? (
        <p data-testid="design-queue-loading" className="px-5 py-6 text-center text-xs text-slate-400"><CircleNotch className="animate-spin inline mr-1.5" size={14} />Comparing assigned designs with extracted methods…</p>
      ) : data.pending.length === 0 ? (
        <p data-testid="design-queue-empty" className="px-5 py-6 text-center text-xs text-slate-400">No corrections pending — every record's design classification matches its extracted methods.</p>
      ) : (
        data.pending.map((e) => <QueueRow key={e.id} e={e} productId={productId} onResolved={onResolved} />)
      )}
      {data && data.resolved.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-100">
          <p className="overline-label text-slate-400 mb-1.5">Resolved</p>
          {data.resolved.map((e) => (
            <p key={e.id} className="text-[10px] text-slate-500">
              {e.asset_name} — {e.status === "accepted" ? `reclassified to ${e.suggested_label}` : `kept as ${e.assigned_label}`} by {e.resolved_by}
              {e.justification && ` · "${e.justification}"`}
            </p>
          ))}
        </div>
      )}
    </section>
  );
};
